import { ethers } from "ethers"

// Base chain provider
const baseProvider = new ethers.JsonRpcProvider("https://mainnet.base.org")

// Alchemy API key for Base chain
const ALCHEMY_BASE_API_KEY = process.env.ALCHEMY_BASE_API_KEY || process.env.NEXT_PUBLIC_ALCHEMY_BASE_API_KEY

// Interface for Base Chain NFT
export interface BaseChainNFT {
  contractAddress: string
  tokenId: string
}

/**
 * Fetches NFTs owned by a wallet on Base chain
 *
 * @param walletAddress The wallet address to check
 * @returns Array of NFTs owned by the wallet
 */
export async function fetchBaseChainNFTs(walletAddress: string): Promise<BaseChainNFT[]> {
  const tokenData: BaseChainNFT[] = []
  console.log(`Fetching Base chain NFTs for wallet: ${walletAddress}`)
  
  // Check if API key is available
  if (!ALCHEMY_BASE_API_KEY) {
    console.error("ALCHEMY_BASE_API_KEY is not defined!")
    console.log("Available environment variables:", {
      ALCHEMY_BASE_API_KEY: process.env.ALCHEMY_BASE_API_KEY,
      NEXT_PUBLIC_ALCHEMY_BASE_API_KEY: process.env.NEXT_PUBLIC_ALCHEMY_BASE_API_KEY
    })
  } else {
    console.log("ALCHEMY_BASE_API_KEY is available:", ALCHEMY_BASE_API_KEY.substring(0, 10) + "...")
  }

  try {
    // Try using Alchemy's Base chain endpoint with better error handling
    try {
      if (!ALCHEMY_BASE_API_KEY) {
        console.log("Skipping Alchemy API call due to missing API key")
        throw new Error("Missing ALCHEMY_BASE_API_KEY")
      }
      
      const url = `https://base-mainnet.g.alchemy.com/nft/v2/${ALCHEMY_BASE_API_KEY}/getNFTs/?owner=${walletAddress}&pageSize=100`
      console.log(`Calling Base chain Alchemy API: ${url}`)

      const response = await fetch(url)

      if (response.ok) {
        const data = await response.json()
        console.log(`Alchemy Base chain found ${data.totalCount} total NFTs`)

        // Process NFTs from Alchemy Base chain
        if (data.ownedNfts && data.ownedNfts.length > 0) {
          data.ownedNfts.forEach((nft: any) => {
            const contractAddress = nft.contract.address.toLowerCase()
            const tokenId = nft.id.tokenId

            console.log(`Found Base NFT: ${contractAddress} - Token ID: ${tokenId}`)

            tokenData.push({
              contractAddress,
              tokenId,
            })
          })
        }

        // Handle pagination if needed
        if (data.totalCount > 100 && data.pageKey) {
          let pageKey = data.pageKey
          console.log(`More than 100 NFTs found, handling pagination with pageKey: ${pageKey}`)

          while (pageKey) {
            const pageUrl = `https://base-mainnet.g.alchemy.com/nft/v2/${ALCHEMY_BASE_API_KEY}/getNFTs/?owner=${walletAddress}&pageSize=100&pageKey=${pageKey}`
            const pageResponse = await fetch(pageUrl)

            if (!pageResponse.ok) break

            const pageData = await pageResponse.json()
            console.log(`Fetched page with ${pageData.ownedNfts ? pageData.ownedNfts.length : 0} NFTs`)

            if (pageData.ownedNfts && pageData.ownedNfts.length > 0) {
              pageData.ownedNfts.forEach((nft: any) => {
                tokenData.push({
                  contractAddress: nft.contract.address.toLowerCase(),
                  tokenId: nft.id.tokenId,
                })
              })
            }

            pageKey = pageData.pageKey
          }
        }
      } else {
        console.error(`Alchemy Base chain API error: ${response.status}`)
        const errorText = await response.text()
        console.error(`Error response: ${errorText}`)
      }
    } catch (error) {
      console.error("Error fetching NFTs from Alchemy Base chain:", error)
    }

    // If we couldn't get data from Alchemy, try using direct contract calls
    if (tokenData.length === 0) {
      console.log("Falling back to direct contract calls for Base chain NFTs")
      try {
        // List of known Base collections to check directly
        const baseCollections = [
          { id: "based-minis", contractAddress: "0xd683f8f08aca669bf9207d72dceb6bac063c34a2" },
          { id: "ftcg", contractAddress: "0x9a291dd80cd601f4c02da3f595d78e4b640e8b56" },
        ]

        for (const collection of baseCollections) {
          try {
            console.log(`Checking collection ${collection.id} with contract ${collection.contractAddress}`)

            // Create a minimal ERC721 interface to check balance
            const erc721Interface = new ethers.Interface([
              "function balanceOf(address owner) view returns (uint256)",
              "function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)",
            ])

            const contract = new ethers.Contract(collection.contractAddress, erc721Interface, baseProvider)

            // Check if wallet owns any tokens from this collection
            const balance = await contract.balanceOf(walletAddress)
            console.log(`Balance of ${collection.id} for wallet: ${balance.toString()}`)

            if (balance > 0) {
              // For each token, add it to our results
              for (let i = 0; i < Math.min(Number(balance), 20); i++) {
                // Increased limit to 20 tokens per collection
                try {
                  const tokenId = await contract.tokenOfOwnerByIndex(walletAddress, i)
                  console.log(`Found token #${i} with ID ${tokenId.toString()} for collection ${collection.id}`)

                  tokenData.push({
                    contractAddress: collection.contractAddress.toLowerCase(),
                    tokenId: tokenId.toString(),
                  })
                } catch (err) {
                  console.error(`Error fetching token ${i} for collection ${collection.id}:`, err)
                }
              }
            }
          } catch (err) {
            console.error(`Error checking collection ${collection.id} on Base chain:`, err)
          }
        }
      } catch (error) {
        console.error("Error fetching NFTs directly from Base chain:", error)
      }
    }
  } catch (error) {
    console.error("Error in Base chain NFT fetching:", error)
  }

  console.log(`Returning ${tokenData.length} Base chain NFTs`)
  return tokenData
}

/**
 * Fetches the floor price for a Base chain NFT collection
 *
 * @param contractAddress The NFT contract address
 * @returns The floor price in ETH or null if unavailable
 */
export async function fetchBaseChainFloorPrice(contractAddress: string, collectionSlug?: string): Promise<number | null> {
  try {
    // Try Alchemy's Base chain endpoint for floor price using v3 API
    let url = `https://base-mainnet.g.alchemy.com/nft/v3/${ALCHEMY_BASE_API_KEY}/getFloorPrice?contractAddress=${contractAddress}`
    
    // Add collectionSlug parameter if provided for better accuracy
    if (collectionSlug) {
      url += `&collectionSlug=${collectionSlug}`
    }

    console.log(`Fetching Base chain floor price from Alchemy v3 for ${contractAddress}${collectionSlug ? ` with slug ${collectionSlug}` : ''}`)
    const response = await fetch(url)

    if (!response.ok) {
      console.error(`Error fetching Base chain floor price for ${contractAddress}: ${response.status}`)
      return null
    }

    const data = await response.json()
    console.log(`Alchemy v3 Base chain floor price response for ${contractAddress}:`, data)

    // Check if we have OpenSea data
    if (data.openSea && data.openSea.floorPrice) {
      console.log(`✅ Alchemy v3 Base chain OpenSea floor price for ${contractAddress}: ${data.openSea.floorPrice} ETH`)
      return data.openSea.floorPrice
    }
    // Fallback to LooksRare if available
    else if (data.looksRare && data.looksRare.floorPrice) {
      console.log(`✅ Alchemy v3 Base chain LooksRare floor price for ${contractAddress}: ${data.looksRare.floorPrice} ETH`)
      return data.looksRare.floorPrice
    }

    console.log(`❌ No floor price data available from Alchemy v3 Base chain for ${contractAddress}`)

    // For specific collections, provide fallback values if API doesn't return data
    if (contractAddress.toLowerCase() === "0x9a291dd80cd601f4c02da3f595d78e4b640e8b56") {
      // FTCG
      console.log(`Using fallback floor price for FTCG: 0.05 ETH`)
      return 0.05 // Fallback floor price
    }

    if (contractAddress.toLowerCase() === "0xd683f8f08aca669bf9207d72dceb6bac063c34a2") {
      // Based Minis
      console.log(`Using fallback floor price for Based Minis: 0.03 ETH`)
      return 0.03 // Fallback floor price
    }

    return null
  } catch (error) {
    console.error(`Error fetching Base chain floor price for ${contractAddress}:`, error)
    return null
  }
}
